import { useEffect, useId, useRef, useState } from "react";
import {
  organization,
  useActiveMemberRole,
  useActiveOrganization,
  useListOrganizations,
  useSession,
} from "./auth-client";
import { useDemoScope } from "./useDemoScope";
import { isPlatformAdminRole } from "../api/platform-roles";

export function OrgSwitcher({ onGo }: { onGo: (r: string) => void }) {
  const { data: session } = useSession();
  const { data: active } = useActiveOrganization();
  const { data: orgs } = useListOrganizations();
  const { data: memberRole } = useActiveMemberRole();
  const demoScope = useDemoScope();
  const isPlatformAdmin = isPlatformAdminRole(session?.user?.role);
  const orgRole = memberRole?.role;
  const canManageOrg = isPlatformAdmin || (!demoScope && (orgRole === "owner" || orgRole === "admin"));
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverId = useId();

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const setActive = async (orgId: string | null) => {
    if (switching) return;
    setSwitching(true);
    setSwitchError(null);
    try {
      const { error } = await organization.setActive({ organizationId: orgId });
      if (error) {
        setSwitchError(error.message ?? "Impossibile cambiare organizzazione");
        return;
      }
      setOpen(false);
    } catch {
      setSwitchError("Impossibile cambiare organizzazione. Riprova.");
    } finally {
      setSwitching(false);
    }
  };

  const orgList = orgs ?? [];
  const label = active ? active.name : "Seleziona azienda";

  return (
    <div className="org-switcher" ref={ref} aria-busy={switching}>
      <button
        type="button"
        className="org-switcher-trigger"
        ref={triggerRef}
        aria-label={`Cambia organizzazione: ${label}`}
        title={label}
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        disabled={switching}
        onClick={() => setOpen(!open)}
      >
        <span className="org-switcher-label">Azienda</span>
        <strong>{label}</strong>
        <span aria-hidden="true" style={{ color: "var(--text-3)", fontSize: 11 }}>▾</span>
      </button>
      {open && (
        <div className="org-switcher-pop" id={popoverId}>
          {orgList.length === 0 && (
            <div className="user-menu-item" style={{ color: "var(--text-3)", cursor: "default" }}>
              Nessuna organizzazione
            </div>
          )}
          {orgList.map((o) => (
            <button
              type="button"
              key={o.id}
              className="user-menu-item"
              disabled={switching}
              aria-pressed={active?.id === o.id}
              onClick={() => void setActive(o.id)}
              style={{ fontWeight: active?.id === o.id ? 600 : 400 }}
            >
              {o.name}
              {active?.id === o.id && <span style={{ marginLeft: "auto", color: "var(--accent)" }}>✓</span>}
            </button>
          ))}
          {switching && <div className="user-menu-item" role="status">Cambio organizzazione…</div>}
          {switchError && <div className="user-menu-item" role="alert" style={{ color: "var(--danger)" }}>{switchError}</div>}
          {canManageOrg && (
            <>
              <div className="user-menu-sep" />
              <button type="button" className="user-menu-item" disabled={switching} onClick={() => { setOpen(false); onGo("orgs"); }}>
                Gestisci aziende
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
